/* eslint-disable react/prop-types */
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import timezone from 'dayjs/plugin/timezone';
import React, { useCallback, useRef, useState } from 'react';

import { Icon, Text } from '@pinpoint/design-kit';

import { noOp } from 'src/functions';
import CONSTANTS from 'src/store/constants';
import { Rbac } from 'src/containers';
import { DeleteFrameworkDialog, EditFrameworkDialog } from 'src/components/dialogs';
import DeprecatedTag from 'src/components/deprecated_tag';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);

export function CellDate({ value }) {
  return (
    <Text textWeight="400" textSize="small" data-tip={dayjs(value).format('HH:mm [GMT]Z (zzz) — ddd, MMM D, YYYY')}>
      {dayjs(value).format('MMM DD, YYYY')}
    </Text>
  );
}

export function CellText({ value, ...props }) {
  return <Text {...props}>{Array.isArray(value) ? value.join(', ') : value}</Text>;
}

export function CellName({ value, row, getDeprecatedTag, ...props }) {
  const { platform } = row?.original?.config_metadata?.model?.refresh ?? {};
  return (
    <>
      <DeprecatedTag tag={getDeprecatedTag(platform)} m={{ r: '0.5rem' }} />
      <Text {...props}>{value}</Text>
    </>
  );
}

export function CellActions({
  framework,
  framework: { id, name, features, refreshes, regulationTags, canDelete },
  editFramework,
  deleteFramework,
  getData,
}) {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const openDeleteModal = useCallback(() => setDeleteModalOpen(true), [setDeleteModalOpen]);
  const closeDeleteModal = useCallback(() => setDeleteModalOpen(false), [setDeleteModalOpen]);
  const openEditModal = useCallback(() => setEditModalOpen(true), [setEditModalOpen]);
  const closeEditModal = useCallback(() => setEditModalOpen(false), [setEditModalOpen]);
  const handleDelete = useCallback(() => deleteFramework(id), [id, deleteFramework]);
  const handleEdit = useCallback((payload) => editFramework(id, payload), [id, editFramework]);

  /** @type {React.RefObject<HTMLAnchorElement>} */
  const downloadRef = useRef(null);

  const handleDownloadFramework = useCallback(() => {
    const columns = [
      { key: 'id' },
      { key: 'name' },
      { key: 'features', data: features.split(',') },
      { key: 'refreshes', data: refreshes },
      { key: 'regulationTags', header: 'regulation_tags', data: regulationTags },
      { key: 'createdAt', header: 'created_at', format: (v) => v.slice(0, 10) },
      { key: 'updatedAt', header: 'updated_at', format: (v) => v.slice(0, 10) },
    ];

    const createDataRow = (_, idx) =>
      columns.map((c) => {
        if (['features', 'refreshes', 'regulationTags'].includes(c.key)) {
          return c.data[idx] ?? '';
        }
        if (idx === 0) {
          if (c.format) return c.format(framework[c.key]);
          return framework[c.key];
        }
        return '';
      });

    const numRows = Math.max(features.split(',').length, refreshes.length, regulationTags.length);
    const rows = [columns.map((c) => c.header || c.key), ...Array.from({ length: numRows }, createDataRow)];
    const processRow = (row) => row.map((item) => `"${String(item).replace(/"/g, '""')}"`).join(',');

    const csvContent = rows.map(processRow).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    if (downloadRef.current) {
      downloadRef.current.href = url;
      downloadRef.current.download = `framework-${name.toLowerCase()}.csv`;
      downloadRef.current.click();
      URL.revokeObjectURL(url);
    }
  }, [features, refreshes, regulationTags, name, framework]);

  return (
    <>
      <Rbac roles={[CONSTANTS.ROLE.SUPER_ADMIN, CONSTANTS.ROLE.ACTUARY]}>
        <Icon
          name="Edit"
          cursor="pointer"
          hoverTransform="scale(1.25)"
          size=".75rem"
          m={{ r: '.3125rem' }}
          onClick={openEditModal}
          data-test="framework-edit-button"
          data-tip="Edit framework"
        />
        <EditFrameworkDialog
          isOpen={editModalOpen}
          actions={{
            close: {
              on: closeEditModal,
            },
            cancel: {
              on: noOp,
            },
            handlers: [
              {
                on: handleEdit,
                after: getData,
              },
            ],
          }}
          title={{
            text: `Edit Framework: ${name}`,
            backgroundColor: 'orange',
            color: 'white',
            textSize: 'title',
            icon: <Icon name="Edit" size="2.375rem" color="white" alignSelf="center" justifySelf="center" />,
          }}
          framework={framework}
        />
      </Rbac>

      <Rbac
        roles={[CONSTANTS.ROLE.SUPER_ADMIN, CONSTANTS.ROLE.ACTUARY]}
        fallback={
          <Icon size=".75rem" viewBox="0 0 50 50" m={{ x: '0' }} color="tabRest" name="Delete" cursor="not-allowed" />
        }
      >
        <Icon
          name="Delete"
          cursor={canDelete ? 'pointer' : 'not-allowed'}
          color={canDelete ? 'tabSelected' : 'tabDisabled'}
          hoverTransform={canDelete ? 'scale(1.25)' : 'none'}
          hoverColor={canDelete ? 'danger800' : 'tabDisabled'}
          size=".75rem"
          viewBox="0 0 50 50"
          m={{ r: '.3125rem' }}
          onClick={canDelete ? openDeleteModal : noOp}
          data-test="framework-delete-button"
          data-tip={canDelete ? 'Delete framework' : 'Framework assigned to one or more models; cannot delete'}
        />
        <DeleteFrameworkDialog
          isOpen={deleteModalOpen}
          actions={{
            close: {
              on: closeDeleteModal,
            },
            cancel: {
              on: noOp,
            },
            handlers: [
              {
                on: handleDelete,
                after: getData,
              },
            ],
          }}
          framework={framework}
        />
      </Rbac>

      <Rbac roles={[CONSTANTS.ROLE.SUPER_ADMIN, CONSTANTS.ROLE.ACTUARY]}>
        <a ref={downloadRef} style={{ display: 'none' }} href="/">
          Download Framework
        </a>
        <Icon
          color="tabSelected"
          viewBox="0 0 50 50"
          name="Download"
          hoverTransform="scale(1.25)"
          size=".75rem"
          cursor="pointer"
          data-tip="Download framework"
          data-test="framework-download-button"
          m={{ r: '0.3125rem' }}
          onClick={handleDownloadFramework}
        />
      </Rbac>
    </>
  );
}
